import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Input } from '@bruno/ui';
import type { WebsiteWithSnippet } from '@bruno/shared';
import { updateWebsite } from '../api/websites';
import { getWebsiteSubscription } from '../api/billing';
import { getTelegramSummary, listTeamAgents } from '../api/team';
import { useAuth } from '../auth/AuthContext';

interface FormState {
  brandName: string;
  greeting: string;
  accentColor: string;
  hidePoweredBy: boolean;
  hiddenUrlsText: string;
  systemPromptOverride: string;
  handoffEnabled: boolean;
}

const MAX_HIDDEN_URLS = 50;
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function fromWebsite(w: WebsiteWithSnippet): FormState {
  return {
    brandName: w.brandName ?? '',
    greeting: w.greeting ?? '',
    accentColor: w.accentColor ?? '',
    hidePoweredBy: w.hidePoweredBy,
    hiddenUrlsText: (w.hiddenUrls ?? []).join('\n'),
    systemPromptOverride: w.systemPromptOverride ?? '',
    handoffEnabled: w.handoffEnabled,
  };
}

export function WidgetCustomizationPanel({ website }: { website: WebsiteWithSnippet }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(() => fromWebsite(website));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Sync if the website prop changes (e.g. after a save round-trip).
  useEffect(() => {
    setForm(fromWebsite(website));
  }, [website]);

  // Resolve which features the current plan grants.
  const sub = useQuery({
    queryKey: ['sub', website.id],
    queryFn: () => getWebsiteSubscription(website.id),
  });
  const features = (sub.data?.plan?.features as Record<string, unknown> | undefined) ?? {};
  const planName = sub.data?.plan?.name ?? 'Free';
  const allowCustomBrand = features.customBrand === true;
  const allowHidePoweredBy = features.hidePoweredBy === true;
  const allowSystemPrompt = features.systemPromptOverride === true;
  const planMaxAgents = sub.data?.plan?.maxAgents ?? 0;
  const allowHandoff = planMaxAgents > 0;

  // Fetch the team + Telegram link state to surface the soft "nobody
  // online" warning the WebsiteDetailPage spec asks for. We only fire
  // these queries while the toggle is meaningful (plan permits agents
  // AND the user has it ON) — they are owner-only endpoints, so we
  // also gate by role to avoid noisy 403s for agents who happen to
  // open this panel.
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const reachabilityRelevant = allowHandoff && form.handoffEnabled && isOwner;
  const teamQuery = useQuery({
    queryKey: ['team', 'agents'],
    queryFn: listTeamAgents,
    enabled: reachabilityRelevant,
    staleTime: 30_000,
  });
  const tgQuery = useQuery({
    queryKey: ['team', 'telegram'],
    queryFn: getTelegramSummary,
    enabled: reachabilityRelevant,
    staleTime: 30_000,
  });
  const agentCount = teamQuery.data?.usedAgents ?? 0;
  const telegramConfigured = !!tgQuery.data?.bot;
  // "Nobody can pick this up" = no human team members AND no telegram
  // bot configured. Either one being present is enough to drop the
  // warning since notifications can land somewhere.
  const noReachableAgents =
    reachabilityRelevant &&
    teamQuery.isSuccess &&
    tgQuery.isSuccess &&
    agentCount === 0 &&
    !telegramConfigured;

  const save = useMutation({
    mutationFn: () => {
      const hiddenUrls = form.hiddenUrlsText
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (hiddenUrls.length > MAX_HIDDEN_URLS) {
        throw new Error(`Up to ${MAX_HIDDEN_URLS} URL patterns are allowed.`);
      }
      const accent = form.accentColor.trim();
      if (accent && !HEX_RE.test(accent)) {
        throw new Error('Accent color must be a 6-digit hex like #4f46e5.');
      }
      return updateWebsite(website.id, {
        brandName: form.brandName.trim() ? form.brandName.trim() : null,
        greeting: form.greeting.trim() ? form.greeting.trim() : null,
        accentColor: accent ? accent : null,
        hidePoweredBy: form.hidePoweredBy,
        hiddenUrls,
        systemPromptOverride: form.systemPromptOverride.trim()
          ? form.systemPromptOverride.trim()
          : null,
        // Only send the handoff flag when the plan actually lets the
        // user edit it. On Free plans the toggle is disabled (and the
        // site defaults to handoffEnabled=true in the DB), so sending
        // the raw value would re-submit `true` and trip the server's
        // plan gate, blocking the entire save. Omitting it leaves the
        // stored value untouched. The API enforces the same rule.
        handoffEnabled: allowHandoff ? form.handoffEnabled : undefined,
      });
    },
    onSuccess: () => {
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      void qc.invalidateQueries({ queryKey: ['websites', website.id] });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Save failed');
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="text-xs text-slate-500">
        Customize how the Bruno widget looks on your site. Plan: <Badge tone="gray">{planName}</Badge>
      </div>

      <Input
        label="Brand name"
        value={form.brandName}
        placeholder={website.name}
        disabled={!allowCustomBrand}
        onChange={(e) => setForm({ ...form, brandName: e.target.value })}
      />
      {!allowCustomBrand ? (
        <p className="text-xs text-slate-500 -mt-2">
          Custom brand requires the Starter plan or higher.
        </p>
      ) : null}

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-slate-700" htmlFor="bruno-greeting">
          Greeting
        </label>
        <textarea
          id="bruno-greeting"
          className="min-h-[72px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          value={form.greeting}
          placeholder="Hi! I'm Bruno. How can I help you with this site?"
          maxLength={500}
          onChange={(e) => setForm({ ...form, greeting: e.target.value })}
        />
      </div>

      <div className="flex items-end gap-3">
        <Input
          label="Accent color"
          type="text"
          placeholder="#4f46e5"
          value={form.accentColor}
          disabled={!allowCustomBrand}
          onChange={(e) => setForm({ ...form, accentColor: e.target.value })}
        />
        <input
          aria-label="Pick accent color"
          type="color"
          className="h-10 w-12 rounded border border-slate-300"
          disabled={!allowCustomBrand}
          value={/^#[0-9a-fA-F]{6}$/.test(form.accentColor) ? form.accentColor : '#4f46e5'}
          onChange={(e) => setForm({ ...form, accentColor: e.target.value })}
        />
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.hidePoweredBy}
          disabled={!allowHidePoweredBy}
          onChange={(e) => setForm({ ...form, hidePoweredBy: e.target.checked })}
        />
        <span>
          Hide &quot;Powered by Bruno&quot; in the widget footer
          {!allowHidePoweredBy ? (
            <span className="ml-2 text-xs text-slate-500">(Starter plan or higher)</span>
          ) : null}
        </span>
      </label>

      <div className="rounded-md border border-slate-200 p-3">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={allowHandoff && form.handoffEnabled}
            disabled={!allowHandoff}
            title={
              !allowHandoff
                ? 'Upgrade to a plan that includes human agents to enable this feature.'
                : undefined
            }
            onChange={(e) => setForm({ ...form, handoffEnabled: e.target.checked })}
          />
          <span className="flex-1">
            <span className="font-medium text-slate-800">Transfer to a human</span>
            {!allowHandoff ? (
              <span className="ml-2 text-xs text-slate-500">
                (Upgrade to a plan that includes human agents to enable.)
              </span>
            ) : null}
            <span className="block text-xs text-slate-500 mt-1">
              When ON, Bruno can hand the conversation off to your team. Visitors who
              ask for a human are surfaced in the team inbox; if no agent picks up
              within the claim window, the leave-a-message form is shown.
            </span>
            {allowHandoff && form.handoffEnabled && !website.handoffAvailable ? (
              <span className="block text-xs text-amber-700 mt-1">
                Saving will activate this feature. (Currently inactive because the
                plan changed.)
              </span>
            ) : null}
            {allowHandoff && form.handoffEnabled && website.handoffAvailable ? (
              <span className="block text-xs text-green-700 mt-1">
                Active. Configure team and notifications under Team &amp; Settings.
              </span>
            ) : null}
            {noReachableAgents ? (
              <span className="block text-xs text-amber-700 mt-1">
                Transfer-to-human is enabled, but you have no agents on the
                team and no Telegram bot configured — visitors who request
                a human will see the leave-a-message form instead. Add an
                agent under Team or connect a Telegram bot under Telegram.
              </span>
            ) : null}
          </span>
        </label>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-slate-700" htmlFor="bruno-hidden-urls">
          Hidden URL patterns (one per line)
        </label>
        <textarea
          id="bruno-hidden-urls"
          className="min-h-[80px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-xs text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          value={form.hiddenUrlsText}
          placeholder={'/admin\n/admin/*\n/checkout/**'}
          onChange={(e) => setForm({ ...form, hiddenUrlsText: e.target.value })}
        />
        <p className="text-xs text-slate-500">
          Bruno will not render on these paths. Use <code>*</code> for one segment, <code>**</code> for any depth.
        </p>
      </div>

      {allowSystemPrompt ? (
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-700" htmlFor="bruno-system-prompt">
            System prompt override (Growth plan)
          </label>
          <textarea
            id="bruno-system-prompt"
            className="min-h-[120px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            value={form.systemPromptOverride}
            placeholder="(Leave empty to use the default Bruno system prompt.)"
            maxLength={5000}
            onChange={(e) => setForm({ ...form, systemPromptOverride: e.target.value })}
          />
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <Button onClick={() => save.mutate()} loading={save.isPending}>
          Save
        </Button>
        {saved ? <span className="text-sm text-green-600">Saved.</span> : null}
        {error ? <span className="text-sm text-red-600">{error}</span> : null}
      </div>
    </div>
  );
}
